import { createContext, useContext, type ReactNode } from "react";
import { useGlobalLoader } from "@/components/GlobalLoader";
import type { ImpressaoItem } from "@/types/impressao-item";
import inovaone_api from "@/services/inovaone_api";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import type { ImpressaoItemResponse } from "@/types/Impressao-item-response";
import { alert } from "@/contexts/AlertModalContext";
import type { SerialResponseItem } from "@/types/serial-response-item";
import { useSeriais } from "../SeriaisContext";
import { useContaConfig } from "../ContaConfigContext";
import type { SerialItem } from "@/types/serial-item";
import { useLocalizacoes } from "../LocalizacoesContext";
import type { LocalizacaoItem } from "@/types/localizacao-nv1-item";
import { useCategorias } from "../CategoriasContext";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import { retry } from "@/utils/retry";
import {
  destinoDosItens,
  ehDestinoExterno,
  montarItemInovaid,
  tagsQueExistem,
} from "@/lib/destinoItens";
import {
  criarItensNoDestino,
  mensagemDoErroDeDestino,
  verificarTagsNoDestino,
} from "@/services/destino_itens_api";

export type ItemContextType = {
  salvarItensNaBase: (
    itens: ImpressaoItem[],
    reimpressao?: boolean,
    forcar?: boolean,
  ) => Promise<void>;
  pesquisarItensNaBase: (
    tags: string[],
    dataInicio?: string,
    dataFim?: string,
    populate?: boolean,
  ) => Promise<ImpressaoItemResponse[]>;
  /** Conferência de EPC repetido antes de imprimir: consulta o destino dos
   *  itens da configuração (iNOVAONE ou iNOVAID) e devolve as tags que já
   *  existem. URL personalizada não tem consulta: devolve lista vazia. */
  conferirTagsCadastradas: (tags: string[]) => Promise<{ tag: string }[]>;
  atualizarSeriaisDeItens: (itens: ImpressaoItem[]) => Promise<void>;
  apagarItensNaBase: (tags: ImpressaoItem[]) => Promise<void>;
  baixarItem: (item: ImpressaoItemResponse) => Promise<void>;
};

export const ItemContext = createContext<ItemContextType | undefined>(
  undefined,
);

export function ItemProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { impressaoConfig, atualizarEtiquetasImpressas } = useImpressaoConfig();
  const { editarSeriais, criarSeriais, obterSerialAtual } = useSeriais();
  const { atualizarNumeroDeImpressoes } = useContaConfig();
  const { localizacoes, criarLocalizacoesNV1 } = useLocalizacoes();
  const { criarCategorias } = useCategorias();

  const apagarItensNaBase = async (tags: ImpressaoItem[]) => {
    setIsLoading(true);
    try {
      let itensParaApagar = [];
      let tags_na_base = await pesquisarItensNaBase(
        tags.map((item) => item.tag),
      );

      if (tags_na_base.length > 0) {
        itensParaApagar = tags_na_base.filter((item) =>
          tags.some((tag) => tag.tag === item.tag),
        );
        for (const tag of itensParaApagar) {
          await inovaone_api.delete(
            `/item/${tag._id}?id_conta=${impressaoConfig?.id_conta}&id_usuario=${impressaoConfig?.id_usuario}`,
          );
        }
      }
      alert.success("Itens apagados com sucesso.");
    } catch (error) {
      console.error("Erro ao apagar itens na base:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const pesquisarItensNaBase = async (
    tags: string[],
    dataInicio: string = "-",
    dataFim: string = "-",
    populate: boolean = false,
  ) => {
    setIsLoading(true);
    tags = tags.filter(
      (tag) =>
        tag && tag.trim() !== "" && !tag.toLowerCase().includes("aguardando"),
    );
    try {
      const params: string[] = [];
      if (dataInicio !== "-") {
        params.push(`data_inicial=${encodeURIComponent(dataInicio)}`);
        if (dataFim !== "-") {
          params.push(`data_final=${encodeURIComponent(dataFim)}`);
        }
      }
      // populate=true → backend faz .populate de id_categoria, id_nivel_loc_*,
      // id_parceiro e id_usuario (devolvendo objetos em vez de strings).
      if (populate) params.push("populate=true");

      const response = await inovaone_api.post<ImpressaoItemResponse[]>(
        `/pesquisar-itens/${impressaoConfig?.id_conta}?${params.join("&")}`,
        [{ tags }],
      );
      return response.data;
    } catch (error) {
      console.error("Erro ao pesquisar itens na base:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const conferirTagsCadastradas = async (
    tags: string[],
  ): Promise<{ tag: string }[]> => {
    if (!ehDestinoExterno(impressaoConfig)) {
      return pesquisarItensNaBase(tags);
    }
    if (destinoDosItens(impressaoConfig) === "personalizado") return [];
    const validas = tags.filter(
      (tag) =>
        tag && tag.trim() !== "" && !tag.toLowerCase().includes("aguardando"),
    );
    setIsLoading(true);
    try {
      const existentes = await verificarTagsNoDestino(
        impressaoConfig?._id,
        validas,
      );
      return tagsQueExistem(validas, existentes).map((tag) => ({ tag }));
    } catch (error) {
      console.error("Erro ao conferir tags no iNOVAID:", error);
      alert.error(
        mensagemDoErroDeDestino(
          error,
          "Não foi possível conferir os EPCs no iNOVAID.",
        ),
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Destino iNOVAID ou URL personalizada: cria os itens pela API do iNOVAONE,
  // que repassa com o token salvo. Os seriais continuam na conta do iNOVAONE
  // (são eles que garantem EPC novo a cada impressão).
  const salvarItensNoDestinoExterno = async (itens: ImpressaoItem[]) => {
    const nomeDestino =
      destinoDosItens(impressaoConfig) === "inovaid"
        ? "iNOVAID"
        : "URL personalizada";
    let resposta;
    try {
      resposta = await retry(
        () =>
          criarItensNoDestino(
            impressaoConfig?._id,
            itens.map(montarItemInovaid),
          ),
        { rotulo: "destino_itens/itens", tentativas: 3, delayMs: 500 },
      );
    } catch (error) {
      throw new Error(
        mensagemDoErroDeDestino(
          error,
          `Não foi possível enviar os itens para o destino (${nomeDestino}).`,
        ),
      );
    }

    try {
      await atualizarSeriaisDeItens(itens);
    } catch (errSerial) {
      console.error("Falha ao atualizar serial após retries:", errSerial);
      throw errSerial;
    }
    try {
      await atualizarNumeroDeImpressoes();
    } catch (errNum) {
      console.error("Falha ao atualizar numero de impressoes:", errNum);
    }
    try {
      await atualizarEtiquetasImpressas(resposta.criados.length);
    } catch (errEtq) {
      console.error("Falha ao atualizar etiquetas impressas:", errEtq);
    }

    if (resposta.recusados.length) {
      const tagsRecusadas = new Set(
        resposta.recusados.map((r) => r.tag.toUpperCase()),
      );
      console.error(
        "[salvarItensNoDestinoExterno] itens recusados:",
        resposta.recusados,
      );
      throw Object.assign(
        new Error(
          `${nomeDestino} recusou ${resposta.recusados.length} item(ns): ${resposta.recusados
            .map((r) => `${r.tag} (${r.motivo})`)
            .join(", ")}`,
        ),
        {
          itensNaoSalvos: itens.filter((item) =>
            tagsRecusadas.has(item.tag.toUpperCase()),
          ),
        },
      );
    }
  };

  const atualizarSeriaisDeItens = async (itens: ImpressaoItem[]) => {
    if (itens.length === 0) return;
    const lista_seriais_formatada: SerialResponseItem[] = [];
    itens.forEach((item) => {
      // obterSerialAtual lê do ref atualizado no SeriaisContext — evita
      // race entre chamadas item-a-item rápidas do ImpressaoComponent.
      const serialExistente = obterSerialAtual(item.codigo);
      const serialExistenteFormatado = lista_seriais_formatada.find(
        (s) => s.gtin === item.codigo,
      );
      if (serialExistenteFormatado) {
        serialExistenteFormatado.serial += 1;
      } else if (serialExistente) {
        lista_seriais_formatada.push({
          gtin: serialExistente.gtin,
          serial: serialExistente.serial + 1,
          _id: serialExistente._id,
          id_conta: serialExistente.id_conta,
        });
      } else if (impressaoConfig?.id_conta) {
        lista_seriais_formatada.push({
          gtin: item.codigo,
          serial: 0,
          _id: "",
          id_conta: impressaoConfig.id_conta,
        });
      }
    });

    const lista_para_editar: SerialResponseItem[] =
      lista_seriais_formatada.filter((serial) => serial._id);
    const lista_para_criar: SerialItem[] = lista_seriais_formatada
      .filter((serial) => !serial._id)
      .map((serial) => ({
        gtin: serial.gtin,
        serial: serial.serial,
        id_conta: serial.id_conta,
      }));

    // Se um dos dois falhar definitivamente (3 retries), relança para o
    // chamador decidir — o ImpressaoComponent registra a falha pra mostrar
    // no toast final.
    if (lista_para_editar.length) await editarSeriais(lista_para_editar);
    if (lista_para_criar.length) await criarSeriais(lista_para_criar);
  };

  const baixarItem = async (item_a_baixar: ImpressaoItemResponse) => {
    if (!impressaoConfig) return;
    try {
      //setIsLoading(true);
      let localizacao_baixados: LocalizacaoItem | undefined = localizacoes.find(
        (loc) =>
          loc.descricao.toLowerCase() === "baixados" ||
          loc.loc_baixados === true,
      );
      if (!localizacao_baixados) {
        const resp_new_loc = await criarLocalizacoesNV1([
          {
            ativo: 1,
            descricao: "Baixados",
            id_conta: impressaoConfig?.id_conta,
            cor: "",
            loc_baixados: true,
            observacao: "",
            tag: "",
          },
        ]);
        localizacao_baixados = resp_new_loc ? resp_new_loc[0] : undefined;
      }
      if (!localizacao_baixados) {
        alert.error(
          "Erro ao baixar item: localização 'Baixados' não encontrada ou criada.",
        );
        return;
      }
      const newItem: ImpressaoItemResponse = {
        ...item_a_baixar,
        id_nivel_loc_1: localizacao_baixados,
      };
      await inovaone_api.post<ImpressaoItemResponse>(`/itemBO`, [newItem]);
      alert.success("Item baixado com sucesso.");
    } catch (error) {
      console.error("Erro ao baixar item:", error);
      alert.error("Erro ao baixar item.");
    } finally {
      //setIsLoading(false);
    }
  };

  const salvarItensNaBase = async (
    itens: ImpressaoItem[],
    reimpressao?: boolean,
    forcar?: boolean,
  ) => {
    try {
      if (impressaoConfig?.modo_teste) {
        // Modo teste não salva itens; só avança o serial se configurado.
        if (impressaoConfig.modo_teste_atualizar_serial) {
          await atualizarSeriaisDeItens(itens);
        }
        return;
      }

      // Função para validar se o EPC contém apenas caracteres hexadecimais
      const isValidHexEpc = (epc: string): boolean => {
        return /^[A-Fa-f0-9]+$/.test(epc);
      };

      // Filtra itens com EPCs válidos e normaliza as vírgulas para pontos no campo quantidade
      let itensParaSalvar = itens
        .filter((item) => isValidHexEpc(item.tag)) // Filtra apenas EPCs válidos
        .map((item) => ({
          ...item,
          quantidade:
            item.quantidade?.toString().replace(",", ".") || item.quantidade,
        }));

      // Log para mostrar quantos itens foram removidos por EPC inválido
      const totalItens = itens.length;
      const itensValidos = itensParaSalvar.length;
      if (totalItens > itensValidos) {
        console.log(
          `${totalItens - itensValidos} item(s) removido(s) por EPC inválido`,
        );
      }

      // Se não há itens válidos para salvar, retorna
      if (itensParaSalvar.length === 0) {
        console.log("Nenhum item válido para salvar.");
        return;
      }

      if (ehDestinoExterno(impressaoConfig)) {
        // A rota do iNOVAID é idempotente por tag: item já existente não é
        // recriado nem duplicado, então não precisa da pesquisa prévia. A URL
        // personalizada recebe os itens como vieram e decide o que fazer.
        await salvarItensNoDestinoExterno(itensParaSalvar);
        return;
      }

      let itensJaNaBase: ImpressaoItem[] = [];

      try {
        //setIsLoading(true);
        const response_tags_na_base = await retry(
          () =>
            inovaone_api.post<ImpressaoItem[]>(
              `/pesquisar-itens/${impressaoConfig?.id_conta}`,
              [{ tags: itensParaSalvar.map((item) => item.tag) }],
            ),
          { rotulo: "pesquisar-itens", tentativas: 3, delayMs: 400 },
        );
        const itensNaBase = response_tags_na_base.data;
        const estaNaBase = (item: ImpressaoItem) =>
          itensNaBase.some((i) => i.tag === item.tag);
        // Filtra os itens que já existem na base
        if (!reimpressao && !forcar) {
          itensParaSalvar = itensParaSalvar.filter((item) => !estaNaBase(item));
        } else if (forcar && !reimpressao) {
          // Forçar sincronização: o que falta na base é CRIADO (antes ia tudo
          // com reimpressao=true e a API não criava nada, então o EPC que
          // faltava continuava faltando); o que já existe só é reenviado às
          // integrações, sem avançar serial nem contador de etiquetas.
          itensJaNaBase = itensParaSalvar.filter(estaNaBase);
          itensParaSalvar = itensParaSalvar.filter((item) => !estaNaBase(item));
        }
      } catch (error) {
        console.error("Erro ao buscar itens na base:", error);
        // Pesquisar falhou definitivamente — sem essa informação não dá pra
        // saber quais itens são novos. Relança para o chamador tratar.
        throw error;
      }

      if (itensParaSalvar.length) {
        try {
          // Processar categorias vazias
          const categoriasVazias = new Map<string, ImpressaoItem[]>();
          const itensComCategoria: ImpressaoItem[] = [];

          // Separar itens com e sem categoria
          for (const item of itensParaSalvar) {
            if (!item.id_categoria || item.id_categoria.trim() === "") {
              const codigo = item.codigo;
              if (!categoriasVazias.has(codigo)) {
                categoriasVazias.set(codigo, []);
              }
              categoriasVazias.get(codigo)?.push(item);
            } else {
              itensComCategoria.push(item);
            }
          }

          // Criar categorias que não existem
          if (categoriasVazias.size > 0) {
            const novasCategorias: NewCategoriaItem[] = [];

            for (const [codigo, itensGrupo] of categoriasVazias.entries()) {
              // Usar o primeiro item para preencher os campos de informação complementar
              const primeiroItem = itensGrupo[0];

              novasCategorias.push({
                ean: codigo,
                descricao: primeiroItem.descricao,
                inf_compl_1: primeiroItem.inf_compl_1,
                inf_compl_2: primeiroItem.inf_compl_2,
                inf_compl_3: primeiroItem.inf_compl_3,
                inf_compl_4: primeiroItem.inf_compl_4,
                inf_compl_5: primeiroItem.inf_compl_5,
                inf_compl_6: primeiroItem.inf_compl_6,
                inf_compl_7: primeiroItem.inf_compl_7,
                inf_compl_8: primeiroItem.inf_compl_8,
                inf_compl_9: primeiroItem.inf_compl_9,
                inf_compl_10: primeiroItem.inf_compl_10,
                inf_compl_11: primeiroItem.inf_compl_11,
                inf_compl_12: primeiroItem.inf_compl_12,
                id_conta: impressaoConfig?.id_conta || "",
              });
            }

            try {
              const categoriaCriadas = await criarCategorias(novasCategorias);

              if (categoriaCriadas && categoriaCriadas.length > 0) {
                // Mapear categorias criadas por EAN
                const mapa_categorias = new Map(
                  categoriaCriadas.map((cat) => [cat.ean, cat._id]),
                );

                // Atualizar itens com os IDs das categorias criadas
                for (const [codigo, itensGrupo] of categoriasVazias.entries()) {
                  const id_categoria = mapa_categorias.get(codigo);
                  if (id_categoria) {
                    for (const item of itensGrupo) {
                      item.id_categoria = id_categoria;
                    }
                    itensComCategoria.push(...itensGrupo);
                  }
                }
              }
            } catch (error) {
              console.error("Erro ao criar categorias:", error);
              alert.error("Erro ao criar categorias dos itens.");
            }
          }

          // Usar os itens processados para salvar
          itensParaSalvar = itensComCategoria;

          // Item cuja categoria o servidor não devolveu com o mesmo EAN (ou
          // cuja criação falhou) não pode ir para a base. Antes ele era
          // descartado em silêncio: a etiqueta saía gravada e o item não
          // existia. Agora vira erro para o chamador registrar e avisar.
          const itensSemCategoria = [...categoriasVazias.values()]
            .flat()
            .filter(
              (item) => !item.id_categoria || item.id_categoria.trim() === "",
            );
          const erroSemCategoria = itensSemCategoria.length
            ? Object.assign(
                new Error(
                  `Categoria não confirmada pelo servidor para ${[
                    ...new Set(itensSemCategoria.map((i) => i.codigo)),
                  ].join(", ")}. Item não salvo na base.`,
                ),
                { itensNaoSalvos: itensSemCategoria },
              )
            : null;
          if (erroSemCategoria) {
            console.error(
              "[salvarItensNaBase] itens sem categoria, não salvos:",
              itensSemCategoria.map((i) => ({ tag: i.tag, codigo: i.codigo })),
            );
          }

          if (itensParaSalvar.length === 0) {
            if (erroSemCategoria) throw erroSemCategoria;
            console.log("Nenhum item com categoria válida para salvar.");
            return;
          }

          // ATUALIZANDO SERIAIS MESMO QUE NAO SUBA ITENS POR QUE FERNANDA
          // USA PARA CONTA CRONOMETRAGEM. Não bloqueia o salvar — se falhar
          // depois de 3 retries, apenas loga (o ImpressaoComponent registra
          // como falha pendente).
          try {
            await atualizarSeriaisDeItens(itensParaSalvar);
          } catch (errSerial) {
            console.error(
              "Falha ao atualizar serial após retries:",
              errSerial,
              itensParaSalvar.map((i) => ({
                tag: i.tag,
                codigo: i.codigo,
              })),
            );
            throw errSerial;
          }

          try {
            await atualizarNumeroDeImpressoes();
          } catch (errNum) {
            console.error(
              "Falha ao atualizar numero de impressoes:",
              errNum,
            );
          }

          let url = `/itemSaveEstoque/${impressaoConfig?.id_conta}?dbMode=one`;
          if (reimpressao) {
            url += `&reimpressao=true`;
          }
          await retry(
            () => inovaone_api.post<ImpressaoItem[]>(url, itensParaSalvar),
            { rotulo: "itemSaveEstoque", tentativas: 3, delayMs: 500 },
          );
          const somaDeItens = itensParaSalvar.length;
          try {
            await atualizarEtiquetasImpressas(somaDeItens);
          } catch (errEtq) {
            console.error(
              "Falha ao atualizar etiquetas impressas:",
              errEtq,
            );
          }
          if (erroSemCategoria) throw erroSemCategoria;
        } catch (error) {
          console.error(
            "Erro ao salvar itens na base:",
            error,
            itensParaSalvar.map((i) => ({ tag: i.tag, codigo: i.codigo })),
          );
          throw error;
        }
      }

      if (itensJaNaBase.length) {
        await retry(
          () =>
            inovaone_api.post<ImpressaoItem[]>(
              `/itemSaveEstoque/${impressaoConfig?.id_conta}?dbMode=one&reimpressao=true`,
              itensJaNaBase,
            ),
          { rotulo: "itemSaveEstoque-reenvio", tentativas: 3, delayMs: 500 },
        );
      }
    } catch (error) {
      console.error("Erro ao salvar itens:", error);
      throw error;
    } finally {
      //setIsLoading(false);
    }
  };

  return (
    <ItemContext.Provider
      value={{
        salvarItensNaBase,
        pesquisarItensNaBase,
        conferirTagsCadastradas,
        atualizarSeriaisDeItens,
        apagarItensNaBase,
        baixarItem,
      }}
    >
      {children}
    </ItemContext.Provider>
  );
}

export function useItem() {
  const context = useContext(ItemContext);
  if (!context) {
    throw new Error("useItem deve ser usado dentro de um ItemProvider");
  }
  return context;
}
